import { Link } from 'react-router-dom';

interface ArticleCardProps {
  id: number;
  title: string;
  excerpt: string;
  coverImage: string;
  category: string;
  categoryName: string;
  publishedAt: string;
  views: number;
  comments: number;
  isHot?: boolean;
  isNew?: boolean;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({
  id,
  title,
  excerpt,
  coverImage,
  category,
  categoryName,
  publishedAt,
  views,
  comments,
  isHot,
  isNew,
  variant = 'default',
}: ArticleCardProps) {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    if (diffHours < 1) return '刚刚';
    if (diffHours < 24) return `${diffHours}小时前`;
    if (diffHours < 48) return '昨天';
    return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
  };

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${id}`}
        className="group block relative overflow-hidden rounded-lg cursor-pointer h-full min-h-[320px]"
      >
        <img
          src={coverImage}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
          <div className="flex items-center gap-2 mb-2">
            <Link
              to={`/category/${category}`}
              onClick={(e) => e.stopPropagation()}
              className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-white cursor-pointer whitespace-nowrap"
            >
              {categoryName}
            </Link>
            {isHot && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50 dark:text-foreground-950 whitespace-nowrap">
                热门
              </span>
            )}
            {isNew && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-500 text-background-50 dark:text-foreground-950 whitespace-nowrap">
                最新
              </span>
            )}
          </div>
          <h3 className="text-lg md:text-xl font-bold text-white mb-2 line-clamp-2 group-hover:text-primary-300 transition-colors">
            {title}
          </h3>
          <div className="flex items-center gap-3 text-xs text-white/70">
            <span>{formatDate(publishedAt)}</span>
            <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatViews(views)}</span>
            <span className="flex items-center gap-1"><i className="ri-chat-3-line"></i>{comments}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link to={`/article/${id}`} className="group flex gap-3 p-2 -mx-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
        <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img src={coverImage} alt={title} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
            {title}
          </h4>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
            <span>{formatDate(publishedAt)}</span>
            <span className="flex items-center gap-0.5"><i className="ri-eye-line text-[10px]"></i>{formatViews(views)}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/article/${id}`} className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer">
      <div className="relative overflow-hidden">
        <img
          src={coverImage}
          alt={title}
          className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 flex gap-1.5">
          <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-primary-500 text-white">
            {categoryName}
          </span>
          {isHot && (
            <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-accent-500 text-background-50 dark:text-foreground-950">
              热门
            </span>
          )}
          {isNew && (
            <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-secondary-500 text-background-50 dark:text-foreground-950">
              最新
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors">
          {title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
          {excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>{formatDate(publishedAt)}</span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatViews(views)}</span>
            <span className="flex items-center gap-1"><i className="ri-chat-3-line"></i>{comments}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}